/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useMemo, useCallback, useState } from 'react';
import { View } from 'react-native';
import {
  CurrenciesView,
  CURRENCY_LIST,
  CurrencyItem,
  DEFAULT_CURRENCY,
} from '@airasia-rn/ui';
import { BottomSheetView } from '@airasia-phoenix/react-native/components';
import { useBottomSheetBackdropWebFix } from '../../hooks/useBottomSheetBackdropWebFix';
import { pd, getColor } from '@airasia-phoenix/react-native/utils';
import { CurrencyListProps } from './types';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native-unistyles';

const CurrencyListView = (props: CurrencyListProps) => {
  const {
    requestClose,
    onDismissed,
    onSelect,
    bottomSheetViewRef,
    selectedCurrency,
    currencyList,
  } = props;

  const { t } = useTranslation('flights');

  const currencyCode = (selectedCurrency ?? DEFAULT_CURRENCY).toUpperCase();
  const resolvedCurrencyList = currencyList?.length
    ? currencyList
    : CURRENCY_LIST;

  const selectedValue = useMemo<CurrencyItem>(() => {
    return (
      resolvedCurrencyList.find((c) => c.id === currencyCode) ?? {
        id: currencyCode,
        primaryText: currencyCode,
      }
    );
  }, [currencyCode, resolvedCurrencyList]);

  const handleSelect = useCallback(
    (item: CurrencyItem) => {
      requestClose();
      onSelect?.(item);
    },
    [onSelect, requestClose],
  );

  const [isOpen, setIsOpen] = useState(false);
  useBottomSheetBackdropWebFix(isOpen);

  return (
    <BottomSheetView
      testID="currency-bottom-sheet"
      showHeader={false}
      showBackdrop={true}
      backdropProps={{
        opacity: 1,
        backgroundColor: getColor(pd.sys.colour.overlay),
      }}
      initialHeight="95%"
      ref={bottomSheetViewRef}
      height="95%"
      onBottomSheetClose={() => {
        setIsOpen(false);
        onDismissed?.();
      }}
      onChange={(index: number) => {
        if (index >= 0) {
          setIsOpen(true);
        } else {
          setIsOpen(false);
        }
      }}
      enablePanDownToClose={false}
      enableContentPanningGesture={false}
    >
      <View
        style={styles.currenciesViewContainer}
        testID="currencies-view-container"
      >
        <CurrenciesView
          testID="currencies-list-view"
          onClose={requestClose}
          currencyTitle={t('hotelListingPage.currencyTitle')}
          currencyList={resolvedCurrencyList}
          selectedValue={selectedValue}
          sortSelectedToTop={false}
          onSelect={handleSelect}
        />
      </View>
    </BottomSheetView>
  );
};

const styles = StyleSheet.create({
  currenciesViewContainer: {
    flex: 1,
    borderTopLeftRadius: pd.ref.space.spacing40.px,
    borderTopRightRadius: pd.ref.space.spacing40.px,
    overflow: 'hidden',
  },
});

export default CurrencyListView;
